// POST /share/cards — подготовка карточки к отправке.
//
// Картинка рисуется на бэкенде (см. apps/api/src/render), а не скриншотится на клиенте:
// у Telegram нет способа передать файл из мини-аппы в чат, зато он умеет скачать картинку
// по публичному URL. Поэтому ручка отдаёт не байты, а набор ссылок и `prepared_message_id`.
import { z } from 'zod'
import { zodiacSignSchema } from '../enums.js'
import { isoDateSchema, isoDateTimeSchema } from './common.js'

/** Три вида карточек, которые продукт умеет рисовать (спеки 03 §4, 05 «Share-карточка», 06 Н·5). */
export const SHARE_CARD_KINDS = ['forecast', 'compat', 'natal'] as const
export const shareCardKindSchema = z.enum(SHARE_CARD_KINDS)
export type ShareCardKind = z.infer<typeof shareCardKindSchema>

/**
 * Формат картинки. `post` — 1080×1350 для пересылки в чат, `story` — 1080×1920 для историй.
 * Соотношения фиксированы Telegram: сторис он показывает 9:16 и обрезает всё остальное.
 */
export const SHARE_FORMATS = ['post', 'story'] as const
export const shareFormatSchema = z.enum(SHARE_FORMATS)
export type ShareFormat = z.infer<typeof shareFormatSchema>

// --- Полезная нагрузка карточек -------------------------------------------------

/** Предсказание дня: дата + текст прогноза (спека 03 §4). */
export const shareForecastCardSchema = z.object({
  kind: z.literal('forecast'),
  date: isoDateSchema,
  text: z.string().min(1).max(600),
})

/**
 * Совместимость: процент, два знака, приговор.
 *
 * Имени второго человека в карточке НЕТ намеренно. Спека 05 рисует оверлайн «{ИМЯ} × {ИМЯ}»,
 * но там же зафиксировано правило приватности («данные чужих людей я никуда не отдаю»), а
 * карточка уходит в публичный чат и лежит по публичной ссылке. Имя того, кто себя не шерил, —
 * это его данные, а не наши; знак зодиака к человеку не привязывает.
 */
export const shareCompatCardSchema = z.object({
  kind: z.literal('compat'),
  score: z.number().int().min(30).max(100),
  sign_a: zodiacSignSchema.nullable(),
  sign_b: zodiacSignSchema.nullable(),
  verdict: z.string().min(1).max(240),
})

/**
 * Натальная карта: имя владелицы и три кита (спека 06, Н·5).
 *
 * Предел имени — 64 символа, ровно как у `first_name` в Telegram: имя приходит оттуда как есть,
 * и более короткая планка означала бы 400 и «поделиться не работает» вместо карточки. В холст
 * его укладывает не контракт, а вёрстка: оверлайн обрезается по числу строк, которое реально
 * помещается над колесом (`render/cards/natalCard.ts`).
 */
export const shareNatalCardSchema = z.object({
  kind: z.literal('natal'),
  name: z.string().min(1).max(64),
  sun: zodiacSignSchema,
  moon: zodiacSignSchema,
  asc: zodiacSignSchema.nullable(),
})

export const shareCardSchema = z.discriminatedUnion('kind', [
  shareForecastCardSchema,
  shareCompatCardSchema,
  shareNatalCardSchema,
])
export type ShareCard = z.infer<typeof shareCardSchema>
export type ShareForecastCard = z.infer<typeof shareForecastCardSchema>
export type ShareCompatCard = z.infer<typeof shareCompatCardSchema>
export type ShareNatalCard = z.infer<typeof shareNatalCardSchema>

// --- POST /share/cards -----------------------------------------------------------

export const prepareShareRequestSchema = z.object({
  card: shareCardSchema,
})
export type PrepareShareRequest = z.infer<typeof prepareShareRequestSchema>

export const prepareShareResponseSchema = z.object({
  /**
   * Идентификатор заранее собранного сообщения для `WebApp.shareMessage` (Bot API 8.0).
   * `null` — бот не поднят (нет BOT_TOKEN) или Telegram отказал: клиент уходит в фолбэк
   * на `t.me/share/url` с `page_url`, где превью даёт og:image.
   */
  prepared_message_id: z.string().nullable(),
  /** Подписанная публичная ссылка на картинку 1080×1350. */
  image_url: z.string().min(1),
  /** Она же в 1080×1920 — для `shareToStory`. */
  story_image_url: z.string().min(1),
  /** Страница результата с og:image/og:title — фолбэк для старых клиентов и браузера. */
  page_url: z.string().min(1),
  /** Диплинк в мини-аппу (`t.me/<bot>?startapp=ref_<id>`), он же кнопка «Открыть Луню». */
  app_url: z.string().min(1),
  /** Когда подпись ссылок протухнет. */
  expires_at: isoDateTimeSchema,
})
export type PrepareShareResponse = z.infer<typeof prepareShareResponseSchema>
